import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Upload, Film, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { api, apiError, API } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const STATUS_LABEL = { publie: "Publié", brouillon: "Brouillon", archive: "Dépublié" };
const STATUS_CLS = { publie: "bg-green-100 text-green-800", brouillon: "bg-amber-100 text-amber-800", archive: "bg-gray-200 text-gray-700" };
const PAGE = 12;

/**
 * Sélecteur canonique de la médiathèque unique.
 * Affiche TOUS les médias du type demandé (publiés et brouillons, avec badge),
 * recherche, filtres de statut, pagination, import en ligne.
 */
export function MediaPicker({ open, onClose, onSelect, kind = "image", selectedId = "" }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const [progress, setProgress] = useState(0);

    const load = useCallback(() => {
        setError("");
        api.get("/admin/media", { params: { kind, status: status || undefined, search: search || undefined, skip: (page - 1) * PAGE, limit: PAGE } })
            .then((r) => setData(r.data))
            .catch((e) => {
                setData(null);
                setError(apiError(e, "Impossible de charger la médiathèque."));
            });
    }, [kind, status, search, page]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const upload = async (f) => {
        if (!f) return;
        setBusy(true);
        setProgress(0);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("title", f.name);
        try {
            const r = await api.post("/admin/media", fd, {
                onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
            });
            toast.success("Média importé (brouillon) — publiez-le dans « Médias & Vidéos » pour le rendre visible publiquement.");
            load();
            onSelect(r.data);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const items = data?.items || [];
    const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-cream max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="media-picker">
                <DialogHeader>
                    <DialogTitle className="font-heading text-forest">
                        Médiathèque — {kind === "video" ? "choisir une vidéo" : "choisir une image"}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-48">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pineneedle/50" />
                        <Input
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Rechercher (titre, description, alt)…"
                            className="pl-8 bg-white"
                            data-testid="picker-search"
                        />
                    </div>
                    <div className="flex gap-1">
                        {[["", "Tous"], ["publie", "Publiés"], ["brouillon", "Brouillons"]].map(([v, l]) => (
                            <button
                                key={v}
                                onClick={() => { setStatus(v); setPage(1); }}
                                className={`px-3 py-1.5 rounded-full text-xs border ${status === v ? "bg-forest text-cream border-forest" : "border-border text-pineneedle"}`}
                                data-testid={`picker-status-${v || "all"}`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="picker-upload">
                        <Upload size={14} /> {busy ? `Import ${progress}%` : "Importer un média"}
                    </Button>
                    <input
                        ref={fileRef}
                        type="file"
                        className="hidden"
                        accept={kind === "video" ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp"}
                        onChange={(e) => upload(e.target.files[0])}
                        data-testid="picker-upload-input"
                    />
                </div>
                {busy && (
                    <div className="h-1.5 rounded-full bg-pearl overflow-hidden" data-testid="picker-upload-progress">
                        <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
                    </div>
                )}
                {error && <p className="text-sm text-destructive" data-testid="picker-error">{error}</p>}
                {!data && !error && <p className="text-sm text-pineneedle py-8 text-center">Chargement de la médiathèque…</p>}
                {data && items.length === 0 && (
                    <p className="rounded-xl border-2 border-dashed border-gold/40 bg-white p-8 text-center text-sm text-pineneedle" data-testid="picker-empty">
                        Aucun {kind === "video" ? "vidéo" : "visuel"} dans cette vue. Importez un média ou changez de filtre.
                    </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map((m) => (
                        <button
                            key={m.media_id}
                            type="button"
                            onClick={() => onSelect(m)}
                            className={`group text-left rounded-xl overflow-hidden border-2 bg-white transition-colors ${selectedId === m.media_id ? "border-gold" : "border-transparent hover:border-gold/50"}`}
                            data-testid={`picker-item-${m.media_id}`}
                        >
                            <div className="relative h-24 bg-pearl flex items-center justify-center overflow-hidden">
                                {m.kind === "video" ? (
                                    m.has_thumbnail ? (
                                        <img src={`${API}/media/thumb/${m.media_id}`} alt={m.alt || m.title} className="w-full h-full object-cover" loading="lazy" />
                                    ) : (
                                        <Film size={22} className="text-forest/50" />
                                    )
                                ) : (
                                    <img src={`${API}/media/file/${m.media_id}`} alt={m.alt || m.title} className="w-full h-full object-cover" loading="lazy" />
                                )}
                                <span className={`absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_CLS[m.status]}`}>{STATUS_LABEL[m.status]}</span>
                            </div>
                            <div className="p-2">
                                <p className="text-xs font-semibold text-forest truncate">{m.title}</p>
                                <p className="text-[10px] text-pineneedle/70">{(m.size_bytes / 1048576).toFixed(1)} Mo{m.width ? ` · ${m.width}×${m.height}` : ""}</p>
                            </div>
                        </button>
                    ))}
                </div>
                {pages > 1 && (
                    <div className="flex items-center justify-center gap-3 pt-2">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="picker-prev"><ChevronLeft size={14} /></Button>
                        <span className="text-xs text-pineneedle">Page {page} / {pages}</span>
                        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="picker-next"><ChevronRight size={14} /></Button>
                    </div>
                )}
                <p className="text-[11px] text-pineneedle/70 flex items-center gap-1.5" data-testid="picker-publish-hint">
                    <AlertTriangle size={12} className="text-gold-dark shrink-0" />
                    Seuls les médias « Publiés » sont visibles sur le site. Un brouillon sélectionné sera affiché après publication dans « Médias & Vidéos ».
                </p>
            </DialogContent>
        </Dialog>
    );
}
